import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle, Gamepad2, TrendingUp, Clock, Award, Zap, Target, GraduationCap, BarChart3 } from 'lucide-react';
import { Helmet } from 'react-helmet';

const AllCIROExamsGuideBlog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Complete CIRO Exams Guide 2025 | CIRE, RSE, ISE & Derivatives Interactive Study</title>
        <meta name="description" content="Master all CIRO exams with our interactive study guides. CIRE, RSE, ISE, and Derivatives certification prep with games, quizzes, mock exams. Pass on the first try with engaging, interactive learning." />
        <meta name="keywords" content="CIRO exams, CIRE exam prep, RSE exam prep, ISE exam prep, Derivatives exam prep, CIRO certification Canada, investment advisor exam, interactive study guides, Canadian securities exams, CIRO licensing" />
        <meta property="og:title" content="Complete CIRO Exams Guide | Interactive Study for All Canadian Securities Certifications" />
        <meta property="og:description" content="Master CIRE, RSE, ISE, and Derivatives with interactive games, quizzes, and comprehensive mock exams. Start from $34.99 CAD." />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://vittafinance.ca/blog/ciro-exams-complete-guide" />
      </Helmet>

      <article style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
      }}>
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          padding: '80px 20px 60px',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.2)',
              padding: '8px 20px',
              borderRadius: '100px',
              marginBottom: '24px',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}>
              CIRO CERTIFICATION GUIDE 2025
            </div>
            <h1 style={{
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 900,
              marginBottom: '24px',
              lineHeight: 1.2
            }}>
              Master All CIRO Exams with Interactive Study Guides
            </h1>
            <p style={{
              fontSize: '20px',
              opacity: 0.95,
              maxWidth: '800px',
              margin: '0 auto 32px',
              lineHeight: 1.6
            }}>
              Your complete guide to CIRE, RSE, ISE, and Derivatives certifications. Choose your path, master your exam, and launch your Canadian investment career with interactive learning
            </p>
            <div style={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Link
                to="/interactive-guides"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: '#D4AF37',
                  color: 'white',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontSize: '18px',
                  fontWeight: 700,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }}
              >
                <GraduationCap size={22} />
                Explore All Interactive Guides
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: '60px 20px'
        }}>
          {/* Introduction */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '20px'
            }}>
              Understanding CIRO Certification Pathways
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '16px'
            }}>
              The <strong>Canadian Investment Regulatory Organization (CIRO)</strong> administers four key proficiency exams for investment professionals in Canada. Each exam serves a specific role in the industry:
            </p>
            <ul style={{
              fontSize: '17px',
              lineHeight: 2,
              color: '#475569',
              paddingLeft: '24px'
            }}>
              <li><strong>CIRE (Canadian Investment Regulatory Examination):</strong> Entry-level licensing for investment representatives</li>
              <li><strong>RSE (Retail Securities Exam):</strong> For advisors serving individual retail investors</li>
              <li><strong>ISE (Institutional Securities Exam):</strong> For professionals serving corporate and institutional clients</li>
              <li><strong>Derivatives Exam:</strong> Required to trade options and futures with clients</li>
            </ul>
          </section>

          {/* Comparison Grid */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '32px',
              textAlign: 'center'
            }}>
              Compare All CIRO Exams
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              marginBottom: '40px'
            }}>
              {[
                {
                  title: 'CIRE Exam',
                  subtitle: 'Entry-Level Certification',
                  color: '#3b82f6',
                  elements: '9 Elements',
                  duration: '2 hours',
                  questions: '110 questions',
                  price: '$34.99 CAD',
                  regularPrice: '$99.99',
                  link: '/books/cire-interactive',
                  best: 'New to industry'
                },
                {
                  title: 'RSE Exam',
                  subtitle: 'Retail Securities',
                  color: '#10b981',
                  elements: '9 Elements',
                  duration: '3 hours',
                  questions: '120 questions',
                  price: '$49.99 CAD',
                  regularPrice: '$129.99',
                  link: '/books/rse-interactive',
                  best: 'Retail advisors'
                },
                {
                  title: 'ISE Exam',
                  subtitle: 'Institutional Securities',
                  color: '#8b5cf6',
                  elements: '7 Elements',
                  duration: '2.5 hours',
                  questions: '100 questions',
                  price: '$49.99 CAD',
                  regularPrice: '$129.99',
                  link: '/books/ise-interactive',
                  best: 'Institutional traders'
                },
                {
                  title: 'Derivatives',
                  subtitle: 'Options & Futures',
                  color: '#f59e0b',
                  elements: '8 Elements',
                  duration: '2 hours',
                  questions: 'Varies',
                  price: '$49.99 CAD',
                  regularPrice: '$129.99',
                  link: '/books/derivatives-interactive',
                  best: 'Options trading'
                },
              ].map((exam, idx) => (
                <div key={idx} style={{
                  background: 'white',
                  borderRadius: '16px',
                  border: `2px solid ${exam.color}`,
                  overflow: 'hidden',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer'
                }}>
                  <div style={{
                    background: exam.color,
                    padding: '24px',
                    textAlign: 'center',
                    color: 'white'
                  }}>
                    <h3 style={{
                      fontSize: '24px',
                      fontWeight: 800,
                      marginBottom: '8px'
                    }}>
                      {exam.title}
                    </h3>
                    <p style={{
                      fontSize: '14px',
                      opacity: 0.95,
                      marginBottom: '12px'
                    }}>
                      {exam.subtitle}
                    </p>
                    <div style={{
                      background: 'rgba(255,255,255,0.2)',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'inline-block'
                    }}>
                      Best for: {exam.best}
                    </div>
                  </div>
                  <div style={{ padding: '28px' }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      marginBottom: '24px'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '15px',
                        color: '#64748b'
                      }}>
                        <span>Elements:</span>
                        <strong style={{ color: '#1e293b' }}>{exam.elements}</strong>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '15px',
                        color: '#64748b'
                      }}>
                        <span>Duration:</span>
                        <strong style={{ color: '#1e293b' }}>{exam.duration}</strong>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '15px',
                        color: '#64748b'
                      }}>
                        <span>Questions:</span>
                        <strong style={{ color: '#1e293b' }}>{exam.questions}</strong>
                      </div>
                    </div>
                    <div style={{
                      textAlign: 'center',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        fontSize: '32px',
                        fontWeight: 900,
                        color: exam.color,
                        marginBottom: '4px'
                      }}>
                        {exam.price}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#64748b',
                        textDecoration: 'line-through'
                      }}>
                        Regular: {exam.regularPrice}
                      </div>
                    </div>
                    <Link
                      to={exam.link}
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        padding: '14px',
                        background: exam.color,
                        color: 'white',
                        borderRadius: '10px',
                        textDecoration: 'none',
                        fontSize: '16px',
                        fontWeight: 700,
                        transition: 'opacity 0.2s'
                      }}
                    >
                      Get Started
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Why Interactive Learning? */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Why Choose Interactive Study Guides?
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px',
              marginBottom: '32px'
            }}>
              {[
                { icon: <Gamepad2 size={36} />, color: '#3b82f6', title: 'Gamified Learning', desc: '40+ interactive games per exam make complex regulatory concepts engaging and memorable through active learning' },
                { icon: <Target size={36} />, color: '#10b981', title: 'Higher Retention', desc: 'Studies show interactive learning improves retention by 60% compared to passive reading methods' },
                { icon: <Clock size={36} />, color: '#f59e0b', title: 'Study Anywhere', desc: 'Mobile-friendly design lets you study during commutes, breaks, or whenever you have spare time' },
                { icon: <BarChart3 size={36} />, color: '#8b5cf6', title: 'Track Progress', desc: 'Detailed diagnostics show your strengths and weaknesses, helping you focus study time efficiently' },
              ].map((feature, idx) => (
                <div key={idx} style={{
                  padding: '32px',
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ color: feature.color, marginBottom: '20px' }}>
                    {feature.icon}
                  </div>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#1e293b',
                    marginBottom: '12px'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    lineHeight: 1.7,
                    color: '#64748b'
                  }}>
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Career Pathways */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Your CIRO Career Pathway
            </h2>
            <p style={{
              fontSize: '18px',
              lineHeight: 1.8,
              color: '#475569',
              marginBottom: '32px'
            }}>
              Understanding which exams to take depends on your career goals in the Canadian investment industry:
            </p>

            <div style={{
              display: 'grid',
              gap: '24px'
            }}>
              {[
                {
                  career: 'Entry-Level Investment Representative',
                  exams: ['CIRE'],
                  description: 'Start your career with basic securities licensing. Work with clients on fundamental investment products.',
                  salary: '$40K-$70K + commissions'
                },
                {
                  career: 'Retail Investment Advisor',
                  exams: ['RSE', 'Derivatives (optional)'],
                  description: 'Advise individual investors on comprehensive securities portfolios. Build long-term client relationships.',
                  salary: '$60K-$120K + commissions'
                },
                {
                  career: 'Institutional Trader/Advisor',
                  exams: ['ISE', 'Derivatives (often required)'],
                  description: 'Serve corporate clients, pension funds, and institutions. Execute large block trades and strategic placements.',
                  salary: '$80K-$200K+ base + bonus'
                },
                {
                  career: 'Portfolio Manager',
                  exams: ['RSE or ISE', 'Derivatives', 'CFA (recommended)'],
                  description: 'Manage investment portfolios with discretionary authority. Use full range of securities including derivatives.',
                  salary: '$100K-$300K+ base + performance fees'
                },
              ].map((path, idx) => (
                <div key={idx} style={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderLeft: '4px solid #3b82f6',
                  borderRadius: '12px',
                  padding: '32px',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '24px',
                  alignItems: 'start'
                }}>
                  <div>
                    <h3 style={{
                      fontSize: '22px',
                      fontWeight: 700,
                      color: '#1e293b',
                      marginBottom: '12px'
                    }}>
                      {path.career}
                    </h3>
                    <p style={{
                      fontSize: '16px',
                      lineHeight: 1.7,
                      color: '#64748b',
                      marginBottom: '16px'
                    }}>
                      {path.description}
                    </p>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      marginBottom: '12px'
                    }}>
                      {path.exams.map((exam, i) => (
                        <span key={i} style={{
                          padding: '6px 14px',
                          background: '#f0f9ff',
                          color: '#1e40af',
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: 600
                        }}>
                          {exam}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{
                    textAlign: 'right',
                    minWidth: '140px'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#64748b',
                      marginBottom: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Typical Salary
                    </div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#10b981'
                    }}>
                      {path.salary}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* What's Included */}
          <section style={{ marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '32px',
              textAlign: 'center'
            }}>
              What's Included in Every Interactive Guide
            </h2>
            <div style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderRadius: '16px',
              padding: '40px',
              border: '2px solid #3b82f6'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '24px'
              }}>
                {[
                  'Interactive games for each element',
                  'Digital flashcards with spaced repetition',
                  'Exam-style practice questions',
                  'Full-length mock exam',
                  'Detailed diagnostic reports',
                  'Element-by-element breakdown',
                  'Targeted study recommendations',
                  'Mobile-friendly design',
                  'Lifetime access',
                  'All future updates included',
                  'No recurring fees',
                  'One-time payment only'
                ].map((item, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '16px',
                    color: '#1e293b',
                    fontWeight: 500
                  }}>
                    <CheckCircle size={22} color="#10b981" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            borderRadius: '20px',
            padding: '48px 40px',
            textAlign: 'center',
            color: 'white'
          }}>
            <h2 style={{
              fontSize: '36px',
              fontWeight: 900,
              marginBottom: '16px'
            }}>
              Start Your CIRO Certification Journey Today
            </h2>
            <p style={{
              fontSize: '20px',
              opacity: 0.95,
              marginBottom: '32px',
              maxWidth: '700px',
              margin: '0 auto 32px'
            }}>
              Choose your exam, master the content with interactive learning, and launch your Canadian investment career
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'center'
            }}>
              <Link
                to="/interactive-guides"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '18px 40px',
                  background: '#D4AF37',
                  color: 'white',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontSize: '20px',
                  fontWeight: 700,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}
              >
                <GraduationCap size={24} />
                View All Interactive Guides
              </Link>
              <p style={{
                fontSize: '15px',
                opacity: 0.9
              }}>
                From $34.99 CAD • Lifetime access • All updates included
              </p>
            </div>
          </section>
          {/* FAQ Section */}
          <section style={{ marginTop: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '24px'
            }}>
              Frequently Asked Questions About CIRO Exams
            </h2>
            
            {[
              {
                q: 'Which CIRO exam should I start with?',
                a: 'Most candidates start with CIRE as it\'s the entry-level exam. However, if you\'re already in the industry or have a sponsor, you might go directly to RSE or ISE depending on your career path. Derivatives is typically taken after you have your base licensing.'
              },
              {
                q: 'Can I take multiple exams at once?',
                a: 'While technically possible, it\'s not recommended. Each exam requires 4-8 weeks of dedicated preparation. We suggest passing one exam before starting preparation for the next.'
              },
              {
                q: 'Are these guides officially approved by CIRO?',
                a: 'These are independent study guides created by exam prep experts. While not officially endorsed by CIRO, they comprehensively cover all exam content based on official CIRO syllabi and have helped thousands pass.'
              },
              {
                q: 'What if I fail an exam?',
                a: 'CIRO allows retakes with waiting periods. Our interactive guides help identify weak areas through diagnostic testing, so you can focus your study efforts for a successful retake. Many users report passing on retakes after using our targeted approach.'
              },
              {
                q: 'Do I need a sponsor to write CIRO exams?',
                a: 'Generally yes, you need to be sponsored by a CIRO member firm to write most exams. However, requirements vary by exam and province. Check with CIRO or your prospective employer for specific requirements.'
              },
              {
                q: 'How long is a CIRO certification valid?',
                a: 'Once you pass a CIRO exam, that proficiency remains on your record. However, you must maintain active registration with a CIRO member firm and complete continuing education requirements to keep your license active.'
              }
            ].map((faq, idx) => (
              <div key={idx} style={{
                marginBottom: '24px',
                padding: '24px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  {faq.q}
                </h3>
                <p style={{
                  fontSize: '16px',
                  lineHeight: 1.7,
                  color: '#64748b',
                  margin: 0
                }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </section>


          {/* All Resources Section */}
          <section style={{ marginTop: '60px', marginBottom: '60px' }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: 800,
              color: '#1e293b',
              marginBottom: '32px',
              textAlign: 'center'
            }}>
              All CIRO Exam Preparation Resources
            </h2>
            
            {/* Online Courses */}
            <div style={{ marginBottom: '48px' }}>
              <h3 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#1e293b',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                📚 Online Courses - Monthly Access
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px'
              }}>
                {[
                  { name: 'CIRE Course', link: 'https://vittafinance.mykajabi.com/offers/5Tzz73ay', color: '#3b82f6' },
                  { name: 'RSE Course', link: 'https://vittafinance.mykajabi.com/offers/2kzQbJim', color: '#10b981' },
                  { name: 'ISE Course', link: 'https://vittafinance.mykajabi.com/offers/W6ezafud', color: '#8b5cf6' },
                  { name: 'Derivatives Course', link: 'https://vittafinance.mykajabi.com/offers/K52zhpSs/checkout', color: '#f59e0b' }
                ].map((course, idx) => (
                  <a
                    key={idx}
                    href={course.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '24px',
                      border: `2px solid ${course.color}`,
                      textDecoration: 'none',
                      textAlign: 'center',
                      transition: 'transform 0.2s'
                    }}
                  >
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#1e293b',
                      marginBottom: '8px'
                    }}>
                      {course.name}
                    </div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 800,
                      color: course.color,
                      marginBottom: '8px'
                    }}>
                      $29.99/mo
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#64748b'
                    }}>
                      Videos, Podcasts & Mock Exams
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Free Study Guides */}
            <div style={{ marginBottom: '48px' }}>
              <h3 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#1e293b',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                🎁 Free Study Guides
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px'
              }}>
                {[
                  { name: 'CIRE Study Guide', link: '/cire-study-guide', phases: '6 phases', weeks: '4-6 weeks' },
                  { name: 'RSE Study Guide', link: '/rse-study-guide', phases: '11 phases', weeks: '6-8 weeks' },
                  { name: 'ISE Study Guide', link: '/ise-study-guide', phases: '11 phases', weeks: '8-10 weeks' },
                  { name: 'Derivatives Study Guide', link: '/derivatives-study-guide', phases: '9 phases', weeks: '8-10 weeks' }
                ].map((guide, idx) => (
                  <Link
                    key={idx}
                    to={guide.link}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '24px',
                      border: '2px solid #10b981',
                      textDecoration: 'none',
                      textAlign: 'center',
                      transition: 'transform 0.2s'
                    }}
                  >
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#1e293b',
                      marginBottom: '8px'
                    }}>
                      {guide.name}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#64748b',
                      marginBottom: '4px'
                    }}>
                      {guide.phases} • {guide.weeks}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#10b981',
                      marginTop: '8px'
                    }}>
                      FREE
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Interactive Books */}
            <div style={{ marginBottom: '48px' }}>
              <h3 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#1e293b',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                🎮 Interactive Study Guides - One-Time Purchase
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px'
              }}>
                {[
                  { name: 'CIRE Interactive', link: '/books/cire-interactive', price: '$34.99', regular: '$99.99', emoji: '📘' },
                  { name: 'RSE Interactive', link: '/books/rse-interactive', price: '$49.99', regular: '$129.99', emoji: '📗' },
                  { name: 'ISE Interactive', link: '/books/ise-interactive', price: '$49.99', regular: '$129.99', emoji: '🏢' },
                  { name: 'Derivatives Interactive', link: '/books/derivatives-interactive', price: '$49.99', regular: '$129.99', emoji: '📊' }
                ].map((book, idx) => (
                  <Link
                    key={idx}
                    to={book.link}
                    style={{
                      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                      borderRadius: '12px',
                      padding: '24px',
                      border: '2px solid #3b82f6',
                      textDecoration: 'none',
                      textAlign: 'center',
                      transition: 'transform 0.2s'
                    }}
                  >
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>{book.emoji}</div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#1e293b',
                      marginBottom: '8px'
                    }}>
                      {book.name}
                    </div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 800,
                      color: '#3b82f6',
                      marginBottom: '4px'
                    }}>
                      {book.price}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#64748b',
                      textDecoration: 'line-through'
                    }}>
                      Regular: {book.regular}
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Other Resources */}
            <div style={{
              background: '#f8fafc',
              borderRadius: '16px',
              padding: '32px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{
                fontSize: '22px',
                fontWeight: 700,
                color: '#1e293b',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                Additional Free Resources
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                <Link
                  to="/diagnostic-tool"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎯</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>Diagnostic Tool</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>Assess your readiness</div>
                </Link>
                <Link
                  to="/derivatives-strategy-lab"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🧪</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>Strategy Lab</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>Options calculator</div>
                </Link>
                <Link
                  to="/exam-comparison"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>Exam Comparison</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>Compare all exams</div>
                </Link>
                <Link
                  to="/blog"
                  style={{
                    padding: '20px',
                    background: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📝</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>Blog & Articles</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>100+ study tips</div>
                </Link>
              </div>
            </div>
          </section>
        </div>
      </article>
    </>
  );
};

export default AllCIROExamsGuideBlog;